import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import { SeniorityLevel } from "./ManageSeniority";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Loading } from "@/components/ui/loading";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { BackLink } from "@/components/brand/BackLink";

const formSchema = z.object({
  hoursOfDelay: z.coerce
    .number()
    .min(0, "Delay hours must be 0 or greater")
    .max(168, "Delay hours cannot exceed 168 (1 week)"),
});

export default function EditSeniority() {
  const { id } = useParams();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      hoursOfDelay: 0,
    },
  });

  const { data: seniorityLevel, isLoading } = useQuery({
    queryKey: ["seniority", id],
    queryFn: async () => {
      const response = await axiosInstance.get<SeniorityLevel>(
        `seniority/${id}`,
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (seniorityLevel) {
      form.reset({
        hoursOfDelay: seniorityLevel.hoursOfDelay,
      });
    }
  }, [seniorityLevel, form]);

  const mutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      const data = {
        id,
        level: seniorityLevel?.level,
        description: seniorityLevel?.description,
        hoursOfDelay: values.hoursOfDelay,
      };
      return axiosInstance.put(`seniority/${id}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Seniority successfully updated",
        variant: "success",
      });
      navigate("/admin/seniority");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Unable to update seniority.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    mutation.mutate(values);
  };

  if (isLoading) return <Loading />;

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <BackLink to="/admin/seniority">Back to Seniority Levels</BackLink>
      </div>

      <h1 className="text-3xl font-bold text-foreground mb-8">
        Edit Seniority {seniorityLevel && seniorityLevel.description}
      </h1>

      <div className="max-w-2xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="hoursOfDelay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delay Hours</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter delay hours"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {mutation.isPending ? (
              <Loading />
            ) : (
              <div className="flex gap-4">
                <Button type="submit">Save Changes</Button>
                <Link to="/admin/seniority">
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </Link>
              </div>
            )}
          </form>
        </Form>
      </div>
    </div>
  );
}
